import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, CalendarDays, RefreshCw, Trash2, Users, Calendar, Sparkles, CloudRain, Layers, X, CheckCircle2, Link2, Plus, ExternalLink } from "lucide-react";
import ScheduleViewer, { SCHEDULE_TYPE_META } from "./ScheduleViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_ORDER = ["regular", "special_event_friday", "rainy_day", "last_week"];
const TYPE_ICONS = { regular: Calendar, special_event_friday: Sparkles, rainy_day: CloudRain, last_week: CalendarDays };
const LAST_WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const DAY_LABEL = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday" };
const GRID_TYPE_LABEL = {
  rainy_day: "Rainy Day",
  special_event_friday: "Special Event Friday",
  last_week_monday: "Last Week · Monday",
  last_week_tuesday: "Last Week · Tuesday",
  last_week_wednesday: "Last Week · Wednesday",
  last_week_thursday: "Last Week · Thursday",
  last_week_friday: "Last Week · Friday",
};

function TypeBadge({ type }) {
  const meta = SCHEDULE_TYPE_META[type] || SCHEDULE_TYPE_META[type === "special_event" ? "special_event_friday" : "regular"];
  const Icon = TYPE_ICONS[type] || TYPE_ICONS[type === "special_event" ? "special_event_friday" : "regular"] || Calendar;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
      <Icon className="w-2.5 h-2.5" /> {meta.label}
    </span>
  );
}

function UploadRow({ type, onUpload, uploading, lastWeekDay, setLastWeekDay }) {
  const meta = SCHEDULE_TYPE_META[type];
  const Icon = TYPE_ICONS[type];
  const isLastWeek = type === "last_week";
  const isGrid = type !== "regular";
  return (
    <div className="border rounded-lg p-3 bg-white space-y-2" data-testid={`schedule-upload-row-${type}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 min-w-[180px]">
          <Icon className="w-4 h-4 text-slate-700" />
          <div>
            <p className="text-sm font-semibold">{meta.label}</p>
            <p className="text-[10px] text-muted-foreground">{isGrid ? "Camp-wide grid workbook — one row per group, tabs like AM/PM" : meta.description}</p>
          </div>
        </div>
        {isLastWeek && (
          <select
            value={lastWeekDay}
            onChange={(e) => setLastWeekDay(e.target.value)}
            className="text-sm border rounded-md px-2 py-1 h-9"
            data-testid="schedule-last-week-day-select"
          >
            {LAST_WEEK_DAYS.map(d => <option key={d} value={d}>{DAY_LABEL[d]}</option>)}
          </select>
        )}
        <div className="flex-1">
          {uploading ? (
            <div className="flex items-center gap-2 text-sm text-sky-700">
              <RefreshCw className="w-4 h-4 animate-spin" />Parsing…
            </div>
          ) : (
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => onUpload(type, e)}
              className="text-sm"
              data-testid={`schedule-upload-input-${type}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * PopUpStormCard — admin uploads + counselor views a single PDF schedule.
 * Standalone from the XLSX flow because the data shape (binary PDF) and the
 * way users consume it (browser PDF viewer in an iframe) differ.
 */
function PopUpStormCard({ adminMode }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedules/pop-up-storm/status`, { headers });
      setStatus(res.data);
    } catch {
      setStatus({ exists: false });
    } finally { setLoading(false); }
  }, []); // intentionally run once on mount

  useEffect(() => { load(); }, [load]);

  // Build a blob URL for the iframe ONLY when the user clicks View. Released
  // on close + unmount so we don't leak object URLs.
  const openPdf = async () => {
    try {
      const res = await axios.get(`${API}/schedules/pop-up-storm/file`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPdf(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load PDF");
    }
  };

  const closePdf = () => {
    setShowPdf(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("File must be a .pdf");
      e.target.value = ""; return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/schedules/pop-up-storm/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${res.data.filename} (${(res.data.size / 1024).toFixed(1)} KB)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove the current Pop Up Storm PDF?")) return;
    try {
      await axios.delete(`${API}/schedules/pop-up-storm`, { headers });
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="border-indigo-200" data-testid="pop-up-storm-card">
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2 text-indigo-800">
          <CloudRain className="w-5 h-5" /> Pop Up Storm PDF
        </CardTitle>
        <CardDescription>
          A single PDF the whole camp follows when a storm rolls in. Admin uploads, everyone can view.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : status?.exists ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-300">PDF on file</Badge>
            <span className="font-medium text-slate-800 truncate max-w-[260px]" data-testid="pop-up-storm-filename">
              {status.filename}
            </span>
            <span className="text-xs text-slate-500">{(status.size / 1024).toFixed(1)} KB</span>
            <Button size="sm" variant="default" className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={openPdf} data-testid="pop-up-storm-view-btn">
              View PDF
            </Button>
            {adminMode && (
              <Button size="sm" variant="ghost" className="text-red-600"
                onClick={onDelete} data-testid="pop-up-storm-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            {adminMode ? "No PDF uploaded yet." : "No Pop Up Storm schedule has been uploaded by admin."}
          </p>
        )}
        {adminMode && (
          <div className="border-2 border-dashed border-indigo-300 rounded-lg p-3 bg-indigo-50/50">
            <p className="text-[11px] text-indigo-900/80 mb-2">
              {status?.exists ? "Re-upload replaces the current PDF." : "Pick a PDF file (max 12 MB)."}
            </p>
            {uploading ? (
              <div className="flex items-center gap-2 text-sm text-indigo-700">
                <RefreshCw className="w-4 h-4 animate-spin" /> Uploading…
              </div>
            ) : (
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={onUpload}
                className="text-sm"
                data-testid="pop-up-storm-upload-input"
              />
            )}
          </div>
        )}

        {/* Fullscreen PDF viewer */}
        {showPdf && pdfUrl && (
          <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" data-testid="pop-up-storm-viewer">
            <div className="bg-white border-b px-3 py-2 flex items-center justify-between">
              <div className="font-semibold text-sm text-indigo-800 truncate">
                <CloudRain className="inline w-4 h-4 mr-1" />
                Pop Up Storm — {status?.filename}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" asChild>
                  <a href={pdfUrl} download={status?.filename || "Pop_Up_Storm.pdf"}
                     data-testid="pop-up-storm-download-link">
                    Download
                  </a>
                </Button>
                <Button size="sm" variant="ghost" onClick={closePdf}
                  data-testid="pop-up-storm-close-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <iframe
              src={pdfUrl}
              title="Pop Up Storm PDF"
              className="flex-1 w-full bg-white"
              data-testid="pop-up-storm-iframe"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * SchedulesPicker — shared page used by Admin + Supervisor.
 * Props:
 *  - title: heading
 *  - groupCodes: array of group codes this viewer should list. If null, lists ALL groups that have schedules.
 *  - onBack
 *  - adminMode: if true, shows upload rows + delete controls
 */
export default function SchedulesPicker({ title, groupCodes = null, onBack, adminMode = false }) {
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]); // [{group_code, types: {regular:{...}, ...}}]
  const [files, setFiles] = useState([]); // admin only
  const [uploading, setUploading] = useState(null); // type currently uploading
  const [viewingGroup, setViewingGroup] = useState(null);
  const [lastWeekDay, setLastWeekDay] = useState("monday");
  // Workbook 2-step upload
  const [workbookPreview, setWorkbookPreview] = useState(null); // {preview_token, matched, skipped, source_filename}
  const [gridStatus, setGridStatus] = useState({}); // camp-wide grids by grid_type (admin)
  const [workbookSubmitting, setWorkbookSubmitting] = useState(false);
  const [workbookConfirmType, setWorkbookConfirmType] = useState("regular");
  const [workbookConfirmDow, setWorkbookConfirmDow] = useState("monday");
  // Last Week dates admin setting
  const [lastWeekDates, setLastWeekDates] = useState(["", "", "", ""]);
  const [lastWeekDatesSaving, setLastWeekDatesSaving] = useState(false);
  // Google Sheets sync
  const [gsSources, setGsSources] = useState([]);
  const [gsAdding, setGsAdding] = useState(false);
  const [gsLabel, setGsLabel] = useState("");
  const [gsUrl, setGsUrl] = useState("");
  const [gsSyncingId, setGsSyncingId] = useState(null);
  const [gsPreviewSourceId, setGsPreviewSourceId] = useState(null); // remembers which source the preview came from

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const schedRes = await axios.get(`${API}/schedules`, { headers });
      setSchedules(schedRes.data || []);
      if (adminMode) {
        try {
          const filesRes = await axios.get(`${API}/schedules/files`, { headers });
          setFiles(filesRes.data || []);
        } catch { /* non-admin guard already handled */ }
        try {
          const lwRes = await axios.get(`${API}/admin/last-week-dates`, { headers });
          const got = lwRes.data?.dates || [];
          const padded = [got[0] || "", got[1] || "", got[2] || "", got[3] || ""];
          setLastWeekDates(padded);
        } catch { /* keep defaults */ }
        try {
          const gsRes = await axios.get(`${API}/admin/schedule-sync/sources`, { headers });
          setGsSources(gsRes.data || []);
        } catch { /* admin only */ }
        try {
          const rgRes = await axios.get(`${API}/schedules/grid`, { headers });
          setGridStatus(rgRes.data?.grids || {});
        } catch { /* silent */ }
      }
    } catch {
      toast.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, [adminMode]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(type);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" };
    try {
      if (type === "regular") {
        // Regular (Summer) schedules use the per-tab workbook flow.
        fd.append("schedule_type", type);
        const res = await axios.post(`${API}/schedules/upload`, fd, { headers });
        toast.success(`Uploaded ${res.data.count} group${res.data.count === 1 ? "" : "s"} → Regular (tabs in workbook)`);
      } else {
        // Rainy Day / Special Event Friday / Last Week use camp-wide grid workbooks.
        const gridType = type === "last_week" ? `last_week_${lastWeekDay}` : type;
        const res = await axios.post(`${API}/schedules/grid/${gridType}/upload`, fd, { headers });
        const tabNames = (res.data.tabs || []).map(t => t.tab_name).join(", ");
        const dowSuffix = type === "last_week" ? ` · ${DAY_LABEL[lastWeekDay]}` : "";
        toast.success(`${SCHEDULE_TYPE_META[type].label}${dowSuffix} grid uploaded — ${res.data.tabs?.length || 0} tab${res.data.tabs?.length === 1 ? "" : "s"} (${tabNames}), ${res.data.group_count} groups`);
      }
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const handleDeleteFile = async (filename, type) => {
    if (!window.confirm(`Delete ${SCHEDULE_TYPE_META[type]?.label || type} schedules from "${filename}"?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/schedules/file`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { filename, schedule_type: type },
      });
      toast.success("Removed");
      loadAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleWorkbookPreview = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setWorkbookSubmitting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/schedules/upload-workbook/preview`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setWorkbookPreview(res.data);
      setWorkbookConfirmType("regular");
      setWorkbookConfirmDow("monday");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setWorkbookSubmitting(false);
      e.target.value = "";
    }
  };

  const handleWorkbookConfirm = async () => {
    if (!workbookPreview?.preview_token) return;
    setWorkbookSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const body = {
        preview_token: workbookPreview.preview_token,
        schedule_type: workbookConfirmType,
      };
      if (workbookConfirmType === "last_week") body.day_of_week = workbookConfirmDow;
      // Route to the Google-sync confirm path when this preview was created
      // by a Sync Now click; the sync endpoint additionally stamps the
      // source's last_sync_status. The persistence path is identical.
      const url = gsPreviewSourceId
        ? `${API}/admin/schedule-sync/confirm`
        : `${API}/schedules/upload-workbook/confirm`;
      if (gsPreviewSourceId) body.source_id = gsPreviewSourceId;
      const res = await axios.post(url, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved ${res.data.saved_count} group${res.data.saved_count === 1 ? "" : "s"} — ${SCHEDULE_TYPE_META[workbookConfirmType]?.label || workbookConfirmType}`);
      setWorkbookPreview(null);
      setGsPreviewSourceId(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirm failed");
    } finally {
      setWorkbookSubmitting(false);
    }
  };

  const handleSaveLastWeekDates = async () => {
    setLastWeekDatesSaving(true);
    try {
      const token = localStorage.getItem("token");
      const cleaned = lastWeekDates.map(s => (s || "").trim()).filter(Boolean);
      await axios.post(`${API}/admin/last-week-dates`, { dates: cleaned }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Last Week dates saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLastWeekDatesSaving(false);
    }
  };

  // ── Google Sheets sync handlers ──
  const handleGsAdd = async () => {
    if (!gsLabel.trim() || !gsUrl.trim()) {
      toast.error("Label and Google Sheets URL are both required");
      return;
    }
    setGsAdding(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/schedule-sync/sources`,
        { label: gsLabel.trim(), url_or_id: gsUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGsLabel("");
      setGsUrl("");
      toast.success("Source saved");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setGsAdding(false);
    }
  };

  const handleGsDelete = async (id, label) => {
    if (!window.confirm(`Remove saved Google Sheets source "${label}"? (Already-saved schedules are not affected.)`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/schedule-sync/sources/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Source removed");
      loadAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleGsSync = async (source) => {
    setGsSyncingId(source.id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/schedule-sync/preview/${source.id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Reuse the same preview-confirm modal that the file-upload flow uses.
      setWorkbookPreview(res.data);
      setWorkbookConfirmType(source.schedule_type || "regular");
      setWorkbookConfirmDow("monday");
      setGsPreviewSourceId(source.id);
      loadAll(); // refresh status badges
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
      loadAll();
    } finally {
      setGsSyncingId(null);
    }
  };

  if (viewingGroup) {
    return <ScheduleViewer groupCode={viewingGroup} onBack={() => setViewingGroup(null)} />;
  }

  // Filter to requested group codes (supervisor case)
  const codeSet = groupCodes ? new Set(groupCodes.map(c => c.toUpperCase())) : null;
  const filtered = codeSet
    ? schedules.filter(s => codeSet.has((s.group_code || "").toUpperCase()))
    : schedules;
  const sorted = [...filtered].sort((a, b) => (a.group_code || "").localeCompare(b.group_code || "", undefined, { numeric: true }));

  return (
    <div className="min-h-screen bg-background p-3 sm:p-6" data-testid="schedules-picker">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="schedules-picker-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            <h1 className="text-lg sm:text-2xl font-bold font-heading">{title || "Schedules"}</h1>
          </div>
          <div className="w-16" />
        </div>

        {/* Pop Up Storm PDF — visible for everyone (admin uploads, counselors view) */}
        <PopUpStormCard adminMode={adminMode} />

        {adminMode && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-sky-600" /> Upload Schedule XLSX
              </CardTitle>
              <CardDescription>
                One XLSX file per variant. Each tab in the workbook must be the group code (e.g. <code className="text-xs bg-muted px-1 rounded">B01</code>, <code className="text-xs bg-muted px-1 rounded">G14</code>, <code className="text-xs bg-muted px-1 rounded">N07</code>).
                Re-uploading a file with the same name replaces only that file's groups for the chosen variant. Tabs named <code className="text-xs bg-muted px-1 rounded">CHECK</code> / <code className="text-xs bg-muted px-1 rounded">master_copy</code> are ignored.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {TYPE_ORDER.map(t => (
                <UploadRow
                  key={t}
                  type={t}
                  onUpload={handleUpload}
                  uploading={uploading === t}
                  lastWeekDay={lastWeekDay}
                  setLastWeekDay={setLastWeekDay}
                />
              ))}

              {Object.keys(gridStatus).length > 0 && (
                <div className="space-y-1.5" data-testid="grid-status-rows">
                  {Object.entries(gridStatus).map(([gt, tabs]) => (
                    <div key={gt} className="border rounded-lg p-2.5 bg-slate-50 flex items-center justify-between gap-3 text-xs" data-testid={`grid-status-row-${gt}`}>
                      <div className="min-w-0">
                        <span className="font-semibold text-slate-800">{GRID_TYPE_LABEL[gt] || gt} grid active:</span>{" "}
                        <span className="text-slate-600 truncate">{tabs[0]?.source_filename}</span>{" "}
                        <span className="text-slate-500">— tabs: {tabs.map(t => t.tab_name).join(", ")} · {tabs[0]?.rows?.length || 0} groups</span>
                      </div>
                      <Button
                        size="sm" variant="outline" className="h-7 text-[11px] border-red-300 text-red-700 shrink-0"
                        data-testid={`grid-delete-btn-${gt}`}
                        onClick={async () => {
                          if (!window.confirm(`Remove the ${GRID_TYPE_LABEL[gt] || gt} grid?`)) return;
                          try {
                            const token = localStorage.getItem("token");
                            await axios.delete(`${API}/schedules/grid/${gt}`, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(`${GRID_TYPE_LABEL[gt] || gt} grid removed`);
                            loadAll();
                          } catch { toast.error("Failed to remove"); }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Workbook (preview + confirm) — Regular schedule type */}
              <div className="border-2 border-dashed border-sky-300 rounded-lg p-3 bg-sky-50/50 mt-3" data-testid="schedule-workbook-row">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-4 h-4 text-sky-700" />
                  <p className="text-sm font-semibold text-sky-900">Workbook upload (preview → confirm)</p>
                </div>
                <p className="text-[11px] text-sky-800/80 mb-2">
                  Upload an XLSX with one tab per group (tab name = group code). System parses tabs, shows you which match real groups, then you confirm to save. Tabs that don't match any group are listed but skipped.
                </p>
                {workbookSubmitting ? (
                  <div className="flex items-center gap-2 text-sm text-sky-700">
                    <RefreshCw className="w-4 h-4 animate-spin" />Working…
                  </div>
                ) : (
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleWorkbookPreview}
                    className="text-sm"
                    data-testid="schedule-workbook-input"
                  />
                )}
              </div>

              {/* Last Week dates admin setting */}
              <div className="border rounded-lg p-3 bg-amber-50/40 mt-4" data-testid="last-week-dates-section">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-4 h-4 text-amber-700" />
                  <p className="text-sm font-semibold text-amber-900">Last Week of Camp — date settings</p>
                </div>
                <p className="text-[11px] text-amber-900/80 mb-2">
                  The 4 ISO dates that count as Mon/Tue/Wed/Thu of the final week. On those dates the system auto-shows the matching Last Week schedule for every group.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {LAST_WEEK_DAYS.map((d, idx) => (
                    <div key={d} className="space-y-1">
                      <label className="text-[10px] font-semibold text-amber-900 uppercase tracking-wider block">{DAY_LABEL[d]}</label>
                      <Input
                        type="date"
                        value={lastWeekDates[idx] || ""}
                        onChange={(e) => {
                          const next = [...lastWeekDates];
                          next[idx] = e.target.value;
                          setLastWeekDates(next);
                        }}
                        className="h-8 text-xs"
                        data-testid={`last-week-date-${d}`}
                      />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={handleSaveLastWeekDates} disabled={lastWeekDatesSaving} className="mt-2 h-8" data-testid="last-week-dates-save">
                  {lastWeekDatesSaving ? "Saving…" : "Save Last Week dates"}
                </Button>
              </div>

              {/* Google Sheets URL sync */}
              <div className="border-2 border-dashed border-emerald-300 rounded-lg p-3 bg-emerald-50/40 mt-4" data-testid="google-sheets-sync-section">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-4 h-4 text-emerald-700" />
                  <p className="text-sm font-semibold text-emerald-900">Google Sheets URL Sync</p>
                </div>
                <p className="text-[11px] text-emerald-900/80 mb-3">
                  Save a public Google Sheets URL (sharing must be set to <strong>“Anyone with the link can view”</strong>) and click Sync Now anytime to pull the latest tabs and re-save matching groups. No file upload needed.
                </p>

                {/* Source list */}
                {gsSources.length > 0 && (
                  <div className="space-y-2 mb-3" data-testid="google-sheets-sources-list">
                    {gsSources.map(src => (
                      <div key={src.id} className="bg-white border rounded-lg p-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`gs-source-${src.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">{src.label}</span>
                            <a
                              href={`https://docs.google.com/spreadsheets/d/${src.sheet_id}/edit`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-emerald-700 inline-flex items-center gap-1 hover:underline"
                              data-testid={`gs-source-link-${src.id}`}
                            >
                              {src.sheet_id.slice(0, 14)}… <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                            {src.last_sync_status === "success" && (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">synced</Badge>
                            )}
                            {src.last_sync_status === "failed" && (
                              <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid={`gs-source-status-failed-${src.id}`}>failed</Badge>
                            )}
                          </div>
                          {src.last_sync_message && (
                            <p className="text-[10px] text-muted-foreground mt-0.5 break-words">{src.last_sync_message}</p>
                          )}
                          {src.last_synced_at && (
                            <p className="text-[10px] text-muted-foreground">
                              Last synced {new Date(src.last_synced_at).toLocaleString("en-US", { timeZone: "America/New_York" })}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleGsSync(src)}
                            disabled={gsSyncingId === src.id}
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            data-testid={`gs-sync-${src.id}`}
                          >
                            {gsSyncingId === src.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                            {gsSyncingId === src.id ? "Syncing…" : "Sync Now"}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleGsDelete(src.id, src.label)}
                            className="text-red-600 h-8"
                            data-testid={`gs-delete-${src.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add source form */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-end">
                  <div>
                    <label className="text-[10px] font-semibold text-emerald-900 uppercase tracking-wider block mb-1">Label</label>
                    <Input
                      value={gsLabel}
                      onChange={(e) => setGsLabel(e.target.value)}
                      placeholder="e.g. Boys Workbook"
                      className="h-8 text-xs"
                      data-testid="gs-add-label"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-emerald-900 uppercase tracking-wider block mb-1">Google Sheets URL</label>
                    <Input
                      value={gsUrl}
                      onChange={(e) => setGsUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                      className="h-8 text-xs"
                      data-testid="gs-add-url"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleGsAdd}
                    disabled={gsAdding}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="gs-add-save"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> {gsAdding ? "Saving…" : "Add Source"}
                  </Button>
                </div>
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-700">Uploaded Files</p>
                  {files.map((f) => (
                    <div key={`${f.source_filename}-${f.schedule_type}`} className="flex items-start justify-between gap-2 bg-slate-50 border rounded-lg p-2 text-xs" data-testid={`schedule-file-${f.schedule_type}-${f.source_filename}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <TypeBadge type={f.schedule_type} />
                          <p className="font-medium text-slate-800 truncate" title={f.source_filename}>{f.source_filename}</p>
                        </div>
                        <p className="text-muted-foreground">{f.count} groups · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}</p>
                        <p className="text-slate-600 break-all">{(f.group_codes || []).join(", ")}</p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteFile(f.source_filename, f.schedule_type)} data-testid={`schedule-file-delete-${f.schedule_type}-${f.source_filename}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Group grid */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Groups with Schedules</CardTitle>
            <CardDescription>
              Click a group to open its schedule. The viewer has tabs for <strong>Regular</strong>, <strong>Special Event</strong> (Friday / Color War / Final Week), and <strong>Rainy Day</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : sorted.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-schedules">
                No schedules uploaded yet{adminMode ? " — upload an XLSX above to get started." : "."}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {sorted.map(s => {
                  const typesAvailable = TYPE_ORDER.filter(t => s.types && s.types[t]);
                  return (
                    <button
                      key={s.group_code}
                      onClick={() => setViewingGroup(s.group_code)}
                      className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 bg-white border-sky-200 hover:border-sky-400 hover:shadow-lg transition-all active:scale-95"
                      data-testid={`schedule-group-${s.group_code}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-sky-700" />
                      </div>
                      <span className="text-sm font-bold text-sky-800">{s.group_code}</span>
                      <div className="flex flex-wrap justify-center gap-1">
                        {typesAvailable.map(t => <TypeBadge key={t} type={t} />)}
                      </div>
                      {typesAvailable.length === 0 && (
                        <Badge variant="outline" className="text-[9px]">No variants</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Workbook preview-confirm modal */}
      {workbookPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="workbook-preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 p-4 border-b">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-sky-700" /> Confirm workbook upload
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">{workbookPreview.source_filename}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setWorkbookPreview(null)} data-testid="workbook-preview-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4 flex-1">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Save as schedule type</label>
                <select
                  value={workbookConfirmType}
                  onChange={(e) => setWorkbookConfirmType(e.target.value)}
                  className="w-full text-sm border rounded-md px-2 py-1.5 h-9"
                  data-testid="workbook-confirm-type-select"
                >
                  {TYPE_ORDER.map(t => (
                    <option key={t} value={t}>{SCHEDULE_TYPE_META[t].label}</option>
                  ))}
                </select>
                {workbookConfirmType === "last_week" && (
                  <div className="mt-2">
                    <label className="text-xs font-semibold text-slate-700 block mb-1">Day of week</label>
                    <select
                      value={workbookConfirmDow}
                      onChange={(e) => setWorkbookConfirmDow(e.target.value)}
                      className="w-full text-sm border rounded-md px-2 py-1.5 h-9"
                      data-testid="workbook-confirm-dow-select"
                    >
                      {LAST_WEEK_DAYS.map(d => <option key={d} value={d}>{DAY_LABEL[d]}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-emerald-800 mb-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Will save ({workbookPreview.matched.length})
                </p>
                <div className="border rounded-lg bg-emerald-50/50 p-2 max-h-40 overflow-y-auto" data-testid="workbook-matched-list">
                  {workbookPreview.matched.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">No tabs matched.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <tbody>
                        {workbookPreview.matched.map((m) => (
                          <tr key={m.group_code} className="border-b last:border-0">
                            <td className="py-1 px-1 font-medium text-emerald-900">{m.tab_name}</td>
                            <td className="py-1 px-1 text-emerald-700">→ {m.group_code}</td>
                            <td className="py-1 px-1 text-emerald-600 text-right">{m.row_count} rows</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              {workbookPreview.skipped.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">Skipped tabs ({workbookPreview.skipped.length}) — didn't match a group</p>
                  <div className="border rounded-lg bg-slate-50 p-2 text-xs text-slate-600 break-words" data-testid="workbook-skipped-list">
                    {workbookPreview.skipped.join(", ")}
                  </div>
                </div>
              )}
              {workbookPreview.empty && workbookPreview.empty.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-800 mb-1">Skipped (empty) ({workbookPreview.empty.length}) — matched a group but had no data, kept existing schedule</p>
                  <div className="border rounded-lg bg-amber-50 p-2 text-xs text-amber-800 break-words" data-testid="workbook-empty-list">
                    {workbookPreview.empty.join(", ")}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t p-3 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setWorkbookPreview(null)} data-testid="workbook-cancel-btn">Cancel</Button>
              <Button
                onClick={handleWorkbookConfirm}
                disabled={workbookSubmitting || workbookPreview.matched.length === 0}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="workbook-confirm-btn"
              >
                {workbookSubmitting ? "Saving…" : `Confirm save (${workbookPreview.matched.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
